import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import OrderIcon from '@assets/icons/orders.svg';
import PhoneIcon from '@assets/icons/phone.svg';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Analytics } from '@/utils/analytics';
import { APP_MODAL_ROUTES, INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import { Badge } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import { EmptyState } from '@/components/empty-state';
import { fennelTrack } from '@/analytics/fennel/hook';
import {
  FlatList,
  Platform,
  SafeAreaView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { FONTS } from '@/styles';
import { formatCurrency, getTotalLineItemPrice } from '@/utils/money';
import { formatDate, momentWithTimezone } from '@/utils/date';
import { InsightsHubStackParams } from '@/navigators/types';
import { ListEmptyState } from '@/components/list-empty-state';
import { ListItemSeparator } from '@/components/list-item';
import { MultiStyleText } from '@/components/multi-style-text';
import { OperatorEmployeeContact, OrderItemListFieldsFragment } from '@/types';
import { OrderItemDataType } from '@/components/orders/order-detail-list';
import { PageContainer } from '@/components/page-container';
import { PhonePickerBottomSheet } from '@/components/insights-hub/phone-picker-bottom-sheet';
import { SearchInput } from '@/components/search-input';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { useCartRedirect } from '@/hooks/insights-hub/use-cart-redirect';
import { useGetOrderForRestaurant } from '@/components/orders/hooks/use-get-order';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigation } from '@react-navigation/native';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';
import { useUserContext } from '@/contexts/user-context';

type Props = StackScreenProps<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.DraftDetails
>;

type NavigationType = StackNavigationProp<
  InsightsHubStackParams,
  APP_MODAL_ROUTES.ItemDetails
>;

const getSearchableFields = (item: OrderItemDataType) => {
  const orderItem = item as OrderItemListFieldsFragment;
  return [
    orderItem.restaurant_display_name ?? '',
    orderItem.variants_pack.external_item_id ?? '',
    orderItem.pack.unit ?? '',
  ];
};

export const DraftDetailsScreen: React.FC<Props> = ({ route }) => {
  const { goBack } = useNavigation<NavigationType>();
  const { navigateToCart } = useCartRedirect();
  const { navigateToItemDetails } = useHubCardNavigation();

  const [styles, theme] = useStyles();
  const { contacts, orderUUID, restaurantUUID, supplierUUID } = route.params;

  const { restaurantUUIDToRelationshipMap } = useUserContext();
  const restaurant = restaurantUUIDToRelationshipMap[restaurantUUID];

  const [operatorContacts, setOperatorContacts] = useState<
    OperatorEmployeeContact[] | null
  >(null);

  const chatUUID = restaurant?.chatUUIDs?.[0];

  const {
    loading,
    order,
    currencyCode,
    summary: { total, unitCount },
  } = useGetOrderForRestaurant(
    orderUUID,
    chatUUID,
    restaurantUUID,
    supplierUUID,
  );

  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  const { filteredData, onDisplayTextChange, searchText, searching } =
    useMultiSearchFilters<OrderItemDataType>({
      data: order?.orders_items ?? [],
      getSearchableFields,
    });

  const onItemPress = useCallback(
    (item: OrderItemDataType) => {
      navigateToItemDetails(
        restaurantUUID,
        supplierUUID,
        item.variants_pack.uuid,
        INSIGHTS_HUB_ROUTES.DraftDetails,
      );
      fennelTrack(AnalyticEvent.ORDER_ITEM_PRESS, {
        isDraft: true,
        metadata: { dataSource: 'order' },
        orderItemUUID: item.uuid,
        orderUUID,
        origin: INSIGHTS_HUB_ROUTES.DraftDetails,
        restaurantUUID,
        supplierUUID,
        variantPackUUID: item.variants_pack.uuid,
      });
    },
    [navigateToItemDetails, orderUUID, restaurantUUID, supplierUUID],
  );

  useEffect(() => {
    fennelTrack(AnalyticEvent.DRAFT_DETAIL_VIEW, {
      orderUUID,
      restaurantUUID,
      screenName: INSIGHTS_HUB_ROUTES.DraftDetails,
      supplierUUID,
    });
  }, [orderUUID, restaurantUUID, supplierUUID]);

  const noSearchResults =
    searchText.length > 0 ? (
      <ListEmptyState
        text={i18n.t('no_results_for', {
          searchTerm: searchText,
        })}
      />
    ) : null;

  return (
    <PageContainer
      containerStyle={styles.container}
      navigationHeaderProps={{
        actions:
          !loading && order
            ? [
                {
                  accessibilityLabel: i18n.t(
                    'insights_hub.current_orders_section.call',
                  ),
                  Icon: () => (
                    <PhoneIcon
                      height={22}
                      stroke={theme.sk.color.icon.primary}
                      width={22}
                    />
                  ),
                  onPress: () => {
                    setOperatorContacts(contacts);
                    fennelTrack(AnalyticEvent.PHONE_PICKER_SHEET_OPEN, {
                      contacts,
                      orderUUID: order?.uuid!,
                      origin: INSIGHTS_HUB_ROUTES.DraftDetails,
                      platform: Platform.OS,
                      restaurantName: restaurant.name,
                      restaurantUUID: restaurant.uuid,
                    });
                  },
                },
              ]
            : undefined,
        BackButtonIcon: () => (
          <TouchableOpacity accessibilityRole="button" onPress={goBack}>
            <ArrowLeftThin fill={theme.sk.color.icon.primary} />
          </TouchableOpacity>
        ),
        centerTitle: true,
        contentFooter:
          !loading && order ? (
            <SearchInput
              autoCorrect={false}
              containerStyle={styles.searchContainer}
              isLoading={searching}
              onChangeText={onDisplayTextChange}
              placeholder={i18n.t('find_in_this_cart')}
              value={searchText}
            />
          ) : null,
        showBackButton: true,
        showShadow: false,
        title: (
          <>
            <Text style={[FONTS.primarySemiBold, styles.header]}>
              {i18n.t('order_summary')}
            </Text>
            <Text style={[FONTS.primary, styles.header2]}>
              {restaurant.name}
            </Text>
          </>
        ),
      }}
    >
      {loading ? (
        <View
          accessibilityState={{ busy: true }}
          style={styles.loadingContainer}
        >
          <ActivityIndicator />
        </View>
      ) : !order ? (
        <EmptyState
          description={i18n.t('order_detail.empty_state.description')}
          icon={<OrderIcon stroke={theme.sk.color.icon.primary} />}
          title={i18n.t('order_detail.empty_state.title')}
        />
      ) : (
        <>
          <FlatList
            data={filteredData}
            keyExtractor={(item) => item.uuid}
            ListEmptyComponent={noSearchResults}
            ListHeaderComponent={() => (
              <>
                <View
                  style={{
                    backgroundColor: theme.sk.color.bg.secondary.default,
                  }}
                >
                  <View style={styles.banner}>
                    <Text style={[FONTS.primarySemiBold, styles.bannerText]}>
                      {i18n.t('insights_hub.draft_details_screen.banner')}
                    </Text>
                  </View>
                </View>
                <View style={styles.orderHeader}>
                  <View style={[styles.row, styles.padding]}>
                    <Text
                      style={[FONTS.primarySemiBold, styles.draftOrderDate]}
                    >
                      {order.restaurant_desired_delivery_time
                        ? formatDate(
                            momentWithTimezone(
                              order.restaurant_desired_delivery_time,
                            ),
                            {
                              day: 'numeric',
                              month: 'short',
                            },
                          )
                        : null}
                    </Text>
                    <Badge
                      label={
                        order.fulfillment_type.charAt(0).toUpperCase() +
                        order.fulfillment_type.slice(1).toLowerCase()
                      }
                    />
                  </View>
                  <View style={[styles.row, styles.padding]}>
                    <MultiStyleText
                      defaultStyle={[FONTS.primary, styles.header]}
                      emphasisStyle={[FONTS.primary, styles.header]}
                      forceSingleLine
                    >
                      {i18n.t(
                        unitCount === 1
                          ? 'unit_count_emphasis'
                          : 'unit_count_emphasis_plural',
                        { count: unitCount },
                      )}
                    </MultiStyleText>
                    <Text style={[FONTS.primarySemiBold, styles.header]}>
                      {total}
                    </Text>
                  </View>
                  <ListItemSeparator style={styles.separator} />
                </View>
              </>
            )}
            renderItem={({ item }) => (
              <TouchableOpacity onPress={() => onItemPress(item)}>
                <OrderItem
                  description={
                    item.variants_pack?.salt?.basicInformation
                      ?.descriptions?.[0]?.value
                  }
                  externalItemID={item.variants_pack.external_item_id!}
                  name={item.restaurant_display_name}
                  orderCurrencyCode={currencyCode ?? supplierCurrencyCode}
                  packUnit={item.pack.unit}
                  quantity={item.order_item_prices?.pack_quantity_at_order ?? 1}
                  unitCount={item.pack.unit_count ?? 1}
                  unitPriceInMicros={
                    item.order_item_prices?.unit_price_at_order_micros ?? 0
                  }
                />
              </TouchableOpacity>
            )}
            showsVerticalScrollIndicator={false}
          />
          <SafeAreaView style={styles.buttonContainer}>
            <ListItemSeparator style={styles.separator} />
            <TouchableOpacity
              onPress={async () => {
                await navigateToCart(restaurant.uuid);
                Analytics.track('insights_hub.draft_details_screen.press', {
                  orderUUID,
                  restaurantName: restaurant.name,
                  restaurantUUID: restaurant.uuid,
                });
              }}
              style={styles.editButton}
            >
              <Text style={[FONTS.primarySemiBold, styles.editButtonText]}>
                {i18n.t('insights_hub.draft_details_screen.button')}
              </Text>
            </TouchableOpacity>
          </SafeAreaView>
          {operatorContacts && operatorContacts.length > 0 ? (
            <PhonePickerBottomSheet
              contacts={operatorContacts}
              onDismiss={() => setOperatorContacts(null)}
            />
          ) : null}
        </>
      )}
    </PageContainer>
  );
};

interface OrderItemProps {
  description?: string | null;
  externalItemID: string;
  name: string;
  orderCurrencyCode: string;
  packUnit: string;
  quantity: number;
  unitCount: number;
  unitPriceInMicros: number;
}

const OrderItem: React.FC<OrderItemProps> = ({
  description,
  externalItemID,
  name,
  orderCurrencyCode,
  packUnit,
  unitPriceInMicros,
  quantity,
  unitCount,
}) => {
  const [styles] = useStyles();

  const packPrice = formatCurrency(
    unitPriceInMicros * unitCount * quantity,
    orderCurrencyCode,
  );
  const totalPrice = formatCurrency(
    getTotalLineItemPrice({
      quantity,
      unitCount,
      unitPriceInMicros,
    }) ?? 0,
    orderCurrencyCode,
  );
  return (
    <View style={styles.itemContainer}>
      <View style={styles.row}>
        <Text
          numberOfLines={1}
          style={[FONTS.primarySemiBold, styles.header, styles.margin]}
        >
          {name}
        </Text>
        <Text style={[FONTS.primary, styles.header]}>{totalPrice}</Text>
      </View>
      {description ? (
        <Text numberOfLines={1} style={[FONTS.primary, styles.description]}>
          {description}
        </Text>
      ) : null}
      <View style={[styles.row, styles.detailsContainer]}>
        <View collapsable={false}>
          <Text style={[FONTS.primary, styles.header2]}>
            {[packUnit, externalItemID].filter(Boolean).join(' • ')}
          </Text>
          <Text style={[FONTS.primarySemiBold, styles.header]}>
            {packPrice}
          </Text>
        </View>
        <View style={styles.quantityContainer}>
          <Text style={[FONTS.primarySemiBold, styles.quantityText]}>
            {quantity}
          </Text>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    backgroundColor: theme.sk.color.bg.warning,
    borderRadius: 4,
    margin: 16,
  },
  bannerText: {
    color: theme.sk.color.text.warning,
    fontSize: 14,
    lineHeight: 21,
    marginHorizontal: 16,
    marginVertical: 8,
    textAlign: 'center',
  },
  buttonContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    marginBottom: 32,
  },
  description: {
    color: theme.sk.color.text.tertiary,
    fontSize: 14,
    lineHeight: 21,
    marginRight: 72,
  },
  detailsContainer: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  draftOrderDate: {
    color: theme.sk.color.text.primary,
    fontSize: 18,
  },
  editButton: {
    backgroundColor: theme.sk.color.bg.button.primary.default,
    borderRadius: 8,
    marginHorizontal: 16,
    padding: 16,
  },
  editButtonText: {
    color: theme.sk.color.text.ondark,
    fontSize: 16,
    textAlign: 'center',
  },
  header: {
    color: theme.sk.color.text.primary,
    fontSize: 16,
  },
  header2: {
    color: theme.sk.color.text.primary,
    fontSize: 14,
  },
  itemContainer: {
    gap: 4,
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  margin: {
    flex: 1,
    lineHeight: 24,
    marginRight: 24,
  },
  orderHeader: {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 8,
    paddingTop: 16,
  },
  padding: {
    paddingHorizontal: 16,
  },
  quantityContainer: {
    alignItems: 'center',
    borderColor: theme.sk.color.border.button.disabled,
    borderRadius: 20,
    borderWidth: 1,
    height: 32,
    justifyContent: 'center',
    width: 40,
  },
  quantityText: {
    color: theme.sk.color.text.tertiary,
    fontSize: 12,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  searchContainer: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    height: 48,
    marginBottom: 8,
  },
  separator: {
    backgroundColor: theme.sk.color.divider.default,
    marginHorizontal: 0,
    marginVertical: 8,
  },
}));
